import { CommonModule } from '@angular/common';
import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  TemplateRef,
  afterNextRender,
  computed,
  contentChild,
  effect,
  inject,
  input,
  output,
  signal,
  viewChild
} from '@angular/core';
import { TranslatePipe } from '@ngx-translate/core';

import { TooltipComponent } from '@shared/components/tooltip/tooltip.component';
import { ContentSizedPanelDirective } from '@shared/directives/content-sized-panel.directive';
import { DropdownDirective } from '@shared/directives/dropdown.directive';

import { PillRect, computeVisibleCount } from './pills-overflow.utils';

export interface PillItem {
  id: string;
  data: unknown;
  /** Optional human-readable label to display instead of `data`. */
  label?: string;
}

export interface PillClickEvent<T = unknown> {
  id: string;
  data: T;
}

@Component({
  selector: 'app-pills-overflow',
  imports: [
    CommonModule,
    TooltipComponent,
    TranslatePipe,
    DropdownDirective,
    ContentSizedPanelDirective
  ],
  templateUrl: './pills-overflow.component.html',
  styles: `
    :host {
      display: contents;
    }

    /* Sized to the longest row, so widening it would only add empty space: only
       the height is resizable until a row turns out not to fit.
       Both maxima are the content itself, so a drag stops once the last row shows
       and never opens onto blank space. The 16rem start is what makes a long list
       scroll rather than cover the table. */
    .pills-dropdown-list {
      width: max-content;
      max-width: min(90vw, 32rem);
      height: 16rem;
      max-height: max-content;
      resize: vertical;
      scrollbar-gutter: stable;
      scrollbar-width: thin;
      scrollbar-color: var(--border-default) transparent;
    }

    .pills-dropdown-list.is-clipped {
      max-width: none;
      resize: both;
    }

    .pills-dropdown-list::-webkit-scrollbar {
      width: 8px;
      height: 8px;
    }

    .pills-dropdown-list::-webkit-scrollbar-track {
      background: transparent;
    }

    .pills-dropdown-list::-webkit-scrollbar-thumb {
      background: var(--border-default);
      border-radius: 4px;
    }

    .pills-dropdown-list::-webkit-scrollbar-thumb:hover {
      background: var(--text-tertiary);
    }
  `
})
export class PillsOverflowComponent<T = unknown> {
  private cdr = inject(ChangeDetectorRef);

  items = input<PillItem[]>([]);
  maxRows = input<number>(3);
  pillHeight = input<number>(24);
  gapSize = input<number>(6);

  pillTemplate = contentChild.required<
    TemplateRef<{
      $implicit: PillItem;
      clickable: boolean;
      onClick: ((item: PillItem) => void) | null;
    }>
  >('pillTemplate');

  pillClick = output<PillClickEvent<T>>();

  container = viewChild.required<ElementRef<HTMLDivElement>>('container');
  measureContainer = viewChild<ElementRef<HTMLDivElement>>('measureContainer');

  containerWidth = signal<number>(0);
  maxHeight = signal<number>(56);
  visibleEntries = signal<PillItem[]>([]);
  allEntries = signal<PillItem[]>([]);
  hiddenCount = signal<number>(0);

  invisibleEntries = computed<PillItem[]>(() => {
    const all = this.allEntries();
    const visible = this.visibleEntries();
    return all.slice(visible.length);
  });

  constructor() {
    effect(() => {
      const rows = this.maxRows();
      const height = this.pillHeight();
      const gap = this.gapSize();
      this.maxHeight.set(rows * height + (rows - 1) * gap);
    });

    effect(() => {
      const items = this.items();
      this.allEntries.set(items);
      this.updateVisiblePills();
    });

    afterNextRender(() => {
      this.setupResizeObserver();
      this.updateContainerWidth();
      setTimeout(() => this.updateVisiblePills(), 0);
    });
  }

  private setupResizeObserver(): void {
    // Coalesce bursts of resize notifications into a single measure pass per frame.
    let scheduled = false;
    const resizeObserver = new ResizeObserver(() => {
      if (scheduled) return;
      scheduled = true;
      requestAnimationFrame(() => {
        scheduled = false;
        this.updateContainerWidth();
        this.updateVisiblePills();
      });
    });

    resizeObserver.observe(this.container().nativeElement);
  }

  private updateContainerWidth(): void {
    const width = this.container().nativeElement.clientWidth;
    this.containerWidth.set(width);
  }

  private updateVisiblePills(): void {
    const measureContainerEl = this.measureContainer()?.nativeElement;
    if (!measureContainerEl) return;

    this.cdr.detectChanges();

    const pills = Array.from(
      measureContainerEl.querySelectorAll('[data-index]')
    ) as HTMLElement[];
    const maxH = this.maxHeight();
    const containerW = this.containerWidth();

    if (pills.length === 0 || containerW === 0) {
      this.visibleEntries.set(this.allEntries());
      this.hiddenCount.set(0);
      return;
    }

    // Batch all layout reads together in a single pass: read the container rect
    // once (it does not change between pills) and every pill rect, then let the
    // pure computeVisibleCount decide. Reading per pill interleaved with the
    // container read forced a synchronous reflow on every iteration.
    const containerRect = measureContainerEl.getBoundingClientRect();
    const rects: PillRect[] = pills.map((pill) => {
      const rect = pill.getBoundingClientRect();
      return { top: rect.top, bottom: rect.bottom, right: rect.right };
    });

    const visibleCount = computeVisibleCount(rects, {
      maxHeight: maxH,
      containerWidth: containerW,
      containerLeft: containerRect.left,
      containerTop: containerRect.top,
      pillHeight: this.pillHeight(),
      gapSize: this.gapSize()
    });

    const items = this.allEntries();
    this.visibleEntries.set(items.slice(0, visibleCount));
    this.hiddenCount.set(Math.max(0, items.length - visibleCount));
  }

  protected handlePillClick(item: PillItem): void {
    this.pillClick.emit({
      id: item.id,
      data: item.data as T
    });
  }
}
